import React from 'react';

interface WelcomeOverlayProps {
  isVisible: boolean;
  onConfirm: () => void;
  watercolorImage: string | null;
  address?: string;
}

export const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({
  isVisible,
  onConfirm,
  watercolorImage,
  address
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
      <div 
        className="rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#F5ECE1' }}
      >
        {/* Header */}
        <div className="text-center pt-8 pb-5 px-6 md:px-10">
          <img 
            src="https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/website_assets//Framemyhome.png" 
            alt="Frame My Home" 
            className="mx-auto mb-4"
            style={{ height: '42px' }}
            onError={(e) => {
              // Fallback to text logo if image fails
              e.currentTarget.style.display = 'none';
              const textLogo = e.currentTarget.nextElementSibling as HTMLElement;
              if (textLogo) textLogo.style.display = 'block';
            }}
          />
          {/* Text-based logo fallback */}
          <div 
            className="text-2xl font-semibold hidden mb-4"
            style={{ 
              color: '#2c2c2c',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
            }}
          >
            Frame My Home
          </div>
          
          {address && (
            <p 
              className="text-base"
              style={{ color: '#666' }}
            >
              {address}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 pb-10">
          {/* Frame Preview */}
          <div className="text-center mb-8">
            <div 
              className="inline-block relative rounded-lg mx-auto w-[300px] h-[200px] md:w-[400px] md:h-[300px]"
              style={{
                backgroundImage: watercolorImage ? `url(${watercolorImage})` : 'none',
                backgroundSize: '60%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: watercolorImage ? 'transparent' : '#f0f0f0'
              }}
            >
              {/* Frame Overlay */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: 'url(https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates//Black_M.png)',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              
              {/* Loading state if no image */}
              {!watercolorImage && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-gray-400 text-sm text-center">
                    <div className="mb-2 text-2xl">🎨</div>
                    <div>Generating...</div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Style Label */}
            <div className="mt-4">
              <span 
                className="hidden md:inline-block font-medium px-3 py-1 rounded-full"
                style={{ 
                  backgroundColor: '#e0d4c3', 
                  color: '#2c2c2c',
                  fontSize: '10px'
                }}
              >
                Watercolor Style
              </span>
            </div>
          </div>

          {/* Greeting Message */}
          <div 
            className="rounded-lg p-6 mb-6 border"
            style={{ 
              backgroundColor: '#F5ECE1',
              borderColor: '#e0d4c3',
              color: '#2c2c2c'
            }}
          >
            <p 
              className="text-lg font-medium mb-4 leading-relaxed"
              style={{ 
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
            >
              We are currently generating additional styles for this property.
            </p>
            <p 
              className="text-base leading-relaxed"
              style={{ color: '#666' }}
            >
              This can take up to a minute. The styles will appear as soon as they become available.
            </p>
          </div>

          {/* Loading Indicator */}
          <div className="flex justify-center mb-6">
            <div className="flex space-x-1">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#2c2c2c' }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ 
                  backgroundColor: '#2c2c2c',
                  animationDelay: '0.2s' 
                }}
              ></div>
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ 
                  backgroundColor: '#2c2c2c',
                  animationDelay: '0.4s' 
                }}
              ></div>
            </div>
          </div>

          {/* Confirm Button */}
          <div className="text-center" style={{ paddingBottom: '14px' }}>
            <button
              onClick={onConfirm}
              className="inline-block py-3 px-6 rounded-md font-medium transition-all duration-200 text-white"
              style={{
                backgroundColor: '#2c2c2c',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1a1a1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2c2c2c';
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeOverlay; 